/**
 * Section: Shop navigation
 * ------------------------------------------------------------------------------
 * Scripts to initiate Shop navigation drawer toggle. Highly coupled with menu-drawer and site-header.
 *
 *
 * @namespace shopNavigation
 */
import { register } from '@shopify/theme-sections';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import cssClasses from '~/helpers/cssClasses';
import { on } from '~/helpers/utils';
/**
 * DOM selectors.
 */

const selectors = {
  documentBody: 'body',
  container: '[js-shop-navigation="container"]',
  siteHeaderContainer: '[js-site-header="container"]',
  siteHeaderWrapper: '[js-site-header="wrapper"]',
  listContainer: '.shop-navigation__list-container',
  siteHeaderMasthead: '.site-header__masthead'
};
/**
 * Register the `example-section` section type.
 */

register('shop-navigation', {
  /**
   * Custom public section method.
   */
  setNodeSelectors() {
    const body = document.querySelector(selectors.body);
    const container = document.querySelector(selectors.container);
    const listContainer = document.querySelectorAll(selectors.listContainer);
    const siteHeaderMasthead = document.querySelector(selectors.siteHeaderMasthead);
    const siteHeaderContainer = document.querySelector(selectors.siteHeaderContainer);
    const siteHeaderWrapper = document.querySelector(selectors.siteHeaderWrapper);
    this.nodeSelectors = {
      body,
      container,
      listContainer,
      siteHeaderMasthead,
      siteHeaderContainer,
      siteHeaderWrapper
    };
  },

  /**
   * Sets container margin top based on site header height.
   */
  setContainerTopMargin() {
    const headerBound = this.nodeSelectors.siteHeaderContainer.getBoundingClientRect();
    this.nodeSelectors.container.style.marginTop = "".concat(headerBound.height, "px");
    this.nodeSelectors.container.style.height = "calc(100% - ".concat(headerBound.height, "px)");
  },

  /**
   * Toggle on shop navigation menu.
   * @param {String} handle - Handle that links between shop nav item to primary menu.
   */
  toggleOnShopNavigationMenu(handle) {
    const selectedMenuByHandle = document.querySelector("[js-shop-navigation=\"".concat(handle, "\"]"));
    selectedMenuByHandle.classList.add(cssClasses.active);
    this.nodeSelectors.container.classList.add(cssClasses.active);
    disableBodyScroll(this.nodeSelectors.body);
  },

  /**
   * Toggle off shop navigation menu.
   */
  toggleOffShopNavigationMenu() {
    this.nodeSelectors.container.classList.remove(cssClasses.active);
    this.nodeSelectors.listContainer.forEach(container => {
      container.classList.remove(cssClasses.active);
    });
    enableBodyScroll(this.nodeSelectors.body);
  },

  /**
   * Set EventBus listeners for each instance.
   */
  setEventBusListeners() {
    Frame.EventBus.listen(['Toggle:shopNavigationMenu:open'], handle => this.toggleOnShopNavigationMenu(handle));
    Frame.EventBus.listen(['Toggle:shopNavigationMenu:close'], () => this.toggleOffShopNavigationMenu());
  },

  /**
  * Set Event listeners for each instance.
  */
  setEventListeners() {
    this.nodeSelectors.listContainer.forEach(element => {
      on('mouseenter', element, () => this.handleLinkOnHover(element));
      on('mouseleave', element, () => this.headerReset());
    });
  },

  /**
   * To keep dropdown navigation open while hovering over shop-navigation items.
   * @param {HTMLElement} element - A navigation line item.
   */
  handleLinkOnHover(element) {
    if (window.innerWidth < 1024) {
      return;
    }

    const handle = element.getAttribute('data-shop-navigation');
    element.classList.add(cssClasses.active);
    element.parentNode.classList.add(cssClasses.active);
    this.nodeSelectors.siteHeaderMasthead.classList.add(cssClasses.hovered);
    this.nodeSelectors.siteHeaderWrapper.classList.add(cssClasses.hovered);
    Frame.EventBus.emit('Toggle:siteNavDropDown:open', handle);
  },

  /**
   * Return header to the default styling state.
   *
   */
  headerReset() {
    this.nodeSelectors.siteHeaderWrapper.classList.remove(cssClasses.hovered);
  },

  /**
   * Callback function when section is loaded via `sections.load()` or
   * by the Theme Editor `shopify:section:load` event.
   */
  onLoad() {
    // Do something when a section instance is loaded
    this.setNodeSelectors();
    this.setEventBusListeners();
    this.setEventListeners();
    this.setContainerTopMargin();
  },

  /**
   * Callback function when section unloaded by the
   * Theme Editor `shopify:section:unload` event.
   */
  onUnload() {// Do something when a section instance is unloaded
  },

  /**
   * Callback function when section is selected by the
   * Theme Editor `shopify:section:select` event.
   */
  onSelect() {// Do something when a section instance is selected
  },

  /**
   * Callback function when section is deselected by the
   * Theme Editor `shopify:section:deselect` event.
   */
  onDeselect() {// Do something when a section instance is selected
  },

  /**
   * Callback function when section block is selected by the
   * Theme Editor `shopify:block:select` event.
   */
  onBlockSelect() {// Do something when a section block is selected
  },

  /**
   * Callback function when section block is deselected by the
   * Theme Editor `shopify:block:deselect` event.
   */
  onBlockDeselect() {// Do something when a section block is deselected
  }

});